import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  Buildings,
  CalendarBlank,
  CaretDown,
  CaretRight,
  CaretUp,
  CheckCircle,
  CircleWavyCheck,
  Clock,
  Copy,
  Infinity as InfinityIcon,
  MagnifyingGlass,
  User,
  Warning,
  X,
} from 'phosphor-react';
import { useSearchParams } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  IAuditoriaCheck,
  IAuditoriaItem,
  useFinanceiroAuditoria,
  useFinanceiroAuditoriaDrill,
} from '../../../../hooks/useFinanceiro';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { Segmented } from '../components/Segmented';
import { FIN } from '../components/theme';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const ACCENT = '#2563EB'; // mesma cor do card da Auditoria no hub

const SEV: Record<string, { cor: string; bg: string; label: string }> = {
  alta: { cor: '#DC2626', bg: '#FEF2F2', label: 'Alta' },
  media: { cor: '#D97706', bg: '#FFFBEB', label: 'Média' },
  info: { cor: '#2563EB', bg: '#EFF6FF', label: 'Baixa' },
};
const SEV_RANK: Record<string, number> = { alta: 0, media: 1, info: 2 };

// Famílias do drill (definem quais colunas são ordenáveis no servidor).
const FAMILIA_B = new Set(['baixa_orfa', 'sem_metodo']);
const FAMILIA_CROSS = new Set(['pago_antes_competencia', 'pago_muito_antes_venc']);
const SORT_COLS: Record<string, Record<string, string>> = {
  M: { cliente: 'pessoa_nome', valor: 'total', pago: 'pago', venc: 'data_vencimento' },
  B: { pago: 'valor_pago', pagoem: 'data_pagamento' },
  X: {},
};
const familiaDe = (chave: string | null) => {
  if (!chave) return 'X';
  if (FAMILIA_B.has(chave)) return 'B';
  if (FAMILIA_CROSS.has(chave)) return 'X';
  return 'M';
};

const fmtData = (s: string | null) => (s ? s.split('-').reverse().join('/') : '—');
const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;
const fmtNum = (n: number | null) => (n == null ? '—' : brl(n));
const corPctOk = (p: number | null) => {
  if (p == null || p >= 95) return '#16A34A';
  if (p >= 85) return '#D97706';
  return '#DC2626';
};

/** Chip informativo do recorte ativo (espelho da topbar — sem controle próprio). */
const ScopeChip = ({
  icon,
  children,
  onClear,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClear?: () => void;
}) => (
  <Flex
    align="center"
    gap={1.5}
    px={2.5}
    py={1}
    border="1px solid"
    borderColor={FIN.line}
    borderRadius="full"
    fontSize="xs"
    fontWeight={600}
    color={FIN.muted}
    bg="rgba(255,255,255,0.6)"
  >
    <Box color={ACCENT}>{icon}</Box>
    {children}
    {onClear && (
      <Box as="button" onClick={onClear} color={FIN.faint} _hover={{ color: '#DC2626' }} ml={0.5}>
        <X size={11} weight="bold" />
      </Box>
    )}
  </Flex>
);

/** Card de um check da auditoria: contagem no recorte + severidade + drill. */
const CheckCard = ({
  check,
  index,
  escopo,
  qAtivo,
  onClick,
}: {
  check: IAuditoriaCheck;
  index: number;
  escopo: 'periodo' | 'historico';
  qAtivo: boolean;
  onClick: () => void;
}) => {
  const sev = SEV[check.severidade] ?? SEV.info;
  const hist = check.qtd_historico ?? 0;
  let dimensao = 'histórico completo';
  if (escopo === 'periodo') {
    dimensao = check.dimensao_data === 'baixa' ? 'por data de baixa' : 'por competência';
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.04 * index }}
      style={{ height: '100%' }}
    >
      <GlassCard
        intensity="medium"
        p={4}
        as="button"
        onClick={onClick}
        textAlign="left"
        h="full"
        w="full"
        cursor="pointer"
        borderLeftWidth="3px"
        borderLeftColor={sev.cor}
        _hover={{ transform: 'translateY(-3px)', boxShadow: 'lg' }}
        transition="all 0.15s"
        aria-label={`${check.titulo}: ${check.qtd} ocorrências — ver detalhes`}
      >
        <Flex align="center" justify="space-between" mb={2.5} gap={2}>
          <Flex align="center" gap={1.5}>
            <Badge
              bg={sev.bg}
              color={sev.cor}
              fontSize="9px"
              fontWeight={800}
              textTransform="uppercase"
              borderRadius="full"
              px={2}
            >
              {sev.label}
            </Badge>
            {qAtivo && check.aplica_q === false && (
              <Badge
                bg="rgba(15,23,42,0.05)"
                color={FIN.faint}
                fontSize="9px"
                fontWeight={700}
                borderRadius="full"
                px={2}
                textTransform="none"
              >
                busca não se aplica
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap={0.5} color={FIN.faint} flexShrink={0}>
            <Text fontSize="10px" fontWeight={700} whiteSpace="nowrap">
              ver ocorrências
            </Text>
            <CaretRight size={11} />
          </Flex>
        </Flex>
        <Flex align="baseline" gap={2} mt={1}>
          <Text
            fontSize="26px"
            fontWeight={800}
            color={sev.cor}
            lineHeight={1}
            className="tabular-nums"
          >
            {check.qtd.toLocaleString('pt-BR')}
          </Text>
          {escopo === 'periodo' && hist > check.qtd && (
            <Text fontSize="11px" fontWeight={600} color={FIN.faint} className="tabular-nums">
              de {hist.toLocaleString('pt-BR')} no histórico
            </Text>
          )}
        </Flex>
        <Text fontSize="sm" fontWeight={700} color={FIN.ink} mt={2} noOfLines={1}>
          {check.titulo}
        </Text>
        <Text fontSize="11px" color={FIN.muted} mt={1} noOfLines={2} minH="30px">
          {check.descricao}
        </Text>
        <Text
          fontSize="10px"
          color={FIN.faint}
          mt={2.5}
          pt={2}
          borderTop="1px solid"
          borderColor={FIN.hair}
        >
          {dimensao}
        </Text>
      </GlassCard>
    </motion.div>
  );
};

/** Tile de KPI da pontualidade (não é moeda). */
const PontKpi = ({ label, valor, cor }: { label: string; valor: string; cor: string }) => (
  <Box
    px={4}
    py={3}
    borderRadius="xl"
    bg="rgba(15,23,42,0.02)"
    border="1px solid"
    borderColor={FIN.hair}
  >
    <Text
      fontSize="10px"
      fontWeight={700}
      color={FIN.faint}
      textTransform="uppercase"
      letterSpacing="0.06em"
    >
      {label}
    </Text>
    <Text fontSize="xl" fontWeight={800} color={cor}>
      {valor}
    </Text>
  </Box>
);

/** Linha do drill (título/baixa problemático) + copiar ID. */
const DrillRow = ({ item, onCopy }: { item: IAuditoriaItem; onCopy: (id: string) => void }) => (
  <Box
    role="group"
    display="grid"
    gridTemplateColumns="minmax(140px,1.8fr) minmax(80px,1fr) 96px 96px 88px 88px"
    gap={2}
    px={3}
    py={2}
    alignItems="center"
    borderBottom="1px solid"
    borderColor={FIN.hair}
    fontSize="xs"
  >
    <Box minW={0}>
      <Flex align="center" gap={1}>
        <Text color={FIN.ink} noOfLines={1}>
          {item.pessoa_nome || (
            <Box as="span" color={FIN.faint}>
              {item.id?.slice(0, 8) ?? '—'}
            </Box>
          )}
        </Text>
        {item.id && (
          <IconButton
            aria-label="Copiar ID"
            icon={<Copy size={12} />}
            size="xs"
            variant="ghost"
            minW="20px"
            h="20px"
            color={FIN.faint}
            opacity={0.25}
            _groupHover={{ opacity: 1 }}
            onClick={() => onCopy(item.id as string)}
          />
        )}
      </Flex>
      {item.detalhe && (
        <Text fontSize="10px" color={FIN.faint} noOfLines={1}>
          {item.detalhe}
        </Text>
      )}
    </Box>
    <Text color={FIN.muted} noOfLines={1}>
      {item.conta_origem || '—'}
    </Text>
    <Text textAlign="right" color={FIN.muted}>
      {fmtNum(item.total)}
    </Text>
    <Text textAlign="right" color={FIN.ink} fontWeight={600}>
      {fmtNum(item.pago)}
    </Text>
    <Text textAlign="right" color={FIN.faint}>
      {fmtData(item.data_vencimento)}
    </Text>
    <Text textAlign="right" color="#059669">
      {fmtData(item.data_pagamento)}
    </Text>
  </Box>
);

export const FinanceiroAuditoriaPage = () => {
  const f = useFinanceiroFilters();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Escopo: 'periodo' respeita a topbar; 'historico' ignora datas (estoque).
  // Default: histórico — problema de dado é estoque; mas se o usuário já
  // escolheu período explicitamente no módulo (pset=1), a tela honra.
  const escopoParam = searchParams.get('escopo');
  let escopo: 'periodo' | 'historico' = 'historico';
  if (escopoParam === 'periodo' || escopoParam === 'historico') escopo = escopoParam;
  else if (searchParams.get('pset') === '1') escopo = 'periodo';
  const setEscopo = (v: string) =>
    setSearchParams(
      prev => {
        const n = new URLSearchParams(prev);
        n.set('escopo', v);
        return n;
      },
      { replace: true },
    );

  // Aba: qualidade (default) | pontualidade — na URL, compartilhável.
  const tab = searchParams.get('tab') === 'pontualidade' ? 'pontualidade' : 'qualidade';
  const setTab = (v: string) =>
    setSearchParams(
      prev => {
        const n = new URLSearchParams(prev);
        if (v === 'qualidade') n.delete('tab');
        else n.set('tab', v);
        return n;
      },
      { replace: true },
    );

  // Auto-flip: mexeu no calendário estando em histórico → vira Período.
  const datesRef = useRef(`${f.dateStart}|${f.dateEnd}`);
  useEffect(() => {
    const cur = `${f.dateStart}|${f.dateEnd}`;
    if (datesRef.current === cur) return;
    datesRef.current = cur;
    if (escopo === 'historico') setEscopo('periodo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.dateStart, f.dateEnd]);

  const { data, isLoading, isError, isFetching, refetch } = useFinanceiroAuditoria(
    f.apiParams,
    escopo,
  );

  // Drill: busca interna (server-side, debounce) + ordenação por coluna.
  const [drillChave, setDrillChave] = useState<string | null>(null);
  const [drillBusca, setDrillBusca] = useState('');
  const [drillQ, setDrillQ] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDrillQ(drillBusca.trim()), 300);
    return () => clearTimeout(t);
  }, [drillBusca]);
  const abrirDrill = (chave: string) => {
    setDrillBusca('');
    setDrillQ('');
    setSort(null);
    setDrillChave(chave);
  };
  const { data: drill, isFetching: drillLoading } = useFinanceiroAuditoriaDrill(
    drillChave,
    f.apiParams,
    { escopo, q: drillQ || undefined, order: sort?.key, orderBy: sort?.dir },
  );

  const pont = data?.pontualidade;
  const uni = data?.universo;
  const pctOk =
    uni && uni.lancamentos_periodo > 0
      ? 100 * (1 - uni.titulos_com_ocorrencia / uni.lancamentos_periodo)
      : null;
  const totalOcorr = (data?.checks ?? []).reduce((s, c) => s + c.qtd, 0);

  const checksAtivos = useMemo(
    () =>
      (data?.checks ?? [])
        .filter(c => c.qtd > 0)
        .sort(
          (a, b) => (SEV_RANK[a.severidade] ?? 9) - (SEV_RANK[b.severidade] ?? 9) || b.qtd - a.qtd,
        ),
    [data],
  );
  const checksLimpos = (data?.checks ?? []).filter(c => c.qtd === 0);
  const semDados =
    escopo === 'periodo' && !!uni && uni.lancamentos_periodo === 0 && uni.baixas_periodo === 0;
  const tudoLimpo = !isLoading && !!data && checksAtivos.length === 0 && !semDados;

  const drillCheck = (data?.checks ?? []).find(c => c.chave === drillChave);
  const drillSev = SEV[drillCheck?.severidade ?? ''] ?? SEV.info;
  const fam = familiaDe(drillChave);
  const sortCols = SORT_COLS[fam];
  const totalReal = drill?.total_real ?? drill?.total_ocorrencias ?? 0;

  const copiarId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast({ title: 'ID copiado', status: 'success', duration: 1500 });
  };
  const clickSort = (key?: string) => {
    if (!key) return;
    setSort(s => {
      if (s?.key === key) return { key, dir: s.dir === 'desc' ? 'asc' : 'desc' };
      return { key, dir: 'desc' };
    });
  };

  let franquiaLabel = 'Consolidado';
  if (f.contaOrigem.length === 1) [franquiaLabel] = f.contaOrigem;
  else if (f.contaOrigem.length > 1) franquiaLabel = `${f.contaOrigem.length} franquias`;

  const headerCells: { label: string; slot?: string; right?: boolean }[] = [
    { label: 'Cliente / ID', slot: 'cliente' },
    { label: 'Franquia' },
    { label: 'Valor', slot: 'valor', right: true },
    { label: 'Pago', slot: 'pago', right: true },
    { label: 'Venc.', slot: 'venc', right: true },
    { label: 'Pago em', slot: 'pagoem', right: true },
  ];

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Financeiro"
          titulo="Auditoria de dados"
          subtitulo="Inconsistências de cadastro do Conta Azul — filtre, investigue e corrija na origem"
          icon={<MagnifyingGlass size={24} weight="fill" />}
          cor={ACCENT}
          info={
            <Text>
              Cada card é um <b>check de qualidade</b>: clique para ver os títulos afetados e
              corrigir no Conta Azul. As diferenças de valor <b>não são juros</b> — são
              inconsistências de cadastro. Use a barra do topo para filtrar por{' '}
              <b>período, franquia ou cliente</b>; &quot;Histórico completo&quot; ignora o período e
              mostra o estoque inteiro de problemas.
            </Text>
          }
        />

        {/* ── ScopeBar: recorte ativo + escopo ─────────────────────────── */}
        <Flex wrap="wrap" gap={2} align="center" mb={5}>
          {escopo === 'periodo' ? (
            <ScopeChip icon={<CalendarBlank size={13} weight="duotone" />}>
              {f.monthLabel}
            </ScopeChip>
          ) : (
            <ScopeChip icon={<InfinityIcon size={13} weight="duotone" />}>
              Histórico completo
            </ScopeChip>
          )}
          <ScopeChip icon={<Buildings size={13} weight="duotone" />}>{franquiaLabel}</ScopeChip>
          {f.q && (
            <ScopeChip icon={<User size={13} weight="duotone" />} onClear={() => f.setQ('')}>
              {f.q}
            </ScopeChip>
          )}
          {isFetching && !isLoading && <Spinner size="xs" color={ACCENT} />}
          <Box ml="auto">
            <Segmented
              value={escopo}
              onChange={setEscopo}
              accent={ACCENT}
              options={[
                { value: 'periodo', label: 'Período' },
                { value: 'historico', label: 'Histórico completo' },
              ]}
            />
          </Box>
        </Flex>

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5}>
            <Text color="#DC2626" fontWeight={600}>
              Não foi possível carregar a auditoria.
            </Text>
            <Button mt={3} size="sm" variant="outline" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </GlassCard>
        )}

        {isLoading ? (
          <Flex justify="center" py={16}>
            <Spinner color={ACCENT} />
          </Flex>
        ) : (
          <Box opacity={isFetching ? 0.55 : 1} transition="opacity 0.15s">
            {/* ── Resumo executivo ─────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={6}>
              <KpiCard
                label="Títulos OK"
                valor={pctOk ?? 0}
                format="percent"
                color={corPctOk(pctOk)}
                icon={<CircleWavyCheck size={20} weight="duotone" />}
                index={0}
                info={
                  <InfoPopover title="Títulos OK">
                    Percentual de títulos do recorte sem <b>nenhuma</b> inconsistência de cadastro
                    (checks de título). Um título com dois problemas conta uma vez.
                  </InfoPopover>
                }
              />
              <KpiCard
                label="Ocorrências"
                valor={totalOcorr}
                format="number"
                color="#D97706"
                icon={<Warning size={20} weight="duotone" />}
                index={1}
                info={
                  <InfoPopover title="Ocorrências">
                    Soma das ocorrências de todos os checks no recorte. Um título pode aparecer em
                    mais de um check.
                  </InfoPopover>
                }
              />
              <KpiCard
                label="Pago em dia"
                valor={pont?.pct_em_dia ?? 0}
                format="percent"
                color="#16A34A"
                icon={<Clock size={20} weight="duotone" />}
                index={2}
                onClick={() => setTab('pontualidade')}
                active={tab === 'pontualidade'}
                info={
                  <InfoPopover title="Pago em dia">
                    Atalho para a aba <b>Pontualidade</b> — % de recebíveis baixados até o
                    vencimento no recorte.
                  </InfoPopover>
                }
              />
            </SimpleGrid>

            {/* ── Abas ─────────────────────────────────────────────────── */}
            <Flex justify="center" mb={4}>
              <Segmented
                value={tab}
                onChange={setTab}
                accent={ACCENT}
                size="sm"
                options={[
                  { value: 'qualidade', label: 'Qualidade dos dados' },
                  { value: 'pontualidade', label: 'Pontualidade' },
                ]}
              />
            </Flex>

            {tab === 'qualidade' && (
              <motion.div
                key="qualidade"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                {semDados && (
                  <GlassCard intensity="medium" p={6} mb={4}>
                    <Text fontWeight={700} color={FIN.ink}>
                      Sem lançamentos nem baixas no período selecionado
                    </Text>
                    <Text fontSize="sm" color={FIN.muted} mt={1}>
                      A base do Conta Azul cobre uma janela limitada — ajuste o período ou veja o
                      estoque inteiro.
                    </Text>
                    <Button
                      mt={3}
                      size="sm"
                      variant="outline"
                      borderRadius="full"
                      onClick={() => setEscopo('historico')}
                    >
                      Ver histórico completo
                    </Button>
                  </GlassCard>
                )}

                {tudoLimpo ? (
                  <GlassCard intensity="medium" p={12} textAlign="center">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                    >
                      <CheckCircle size={48} weight="duotone" color="#16A34A" />
                    </motion.div>
                    <Text fontWeight={800} fontSize="lg" color={FIN.ink} mt={3}>
                      Base limpa
                    </Text>
                    <Text fontSize="sm" color={FIN.muted} mt={1}>
                      Nenhuma inconsistência encontrada nos 8 checks para o recorte selecionado.
                    </Text>
                    {escopo === 'periodo' && (
                      <Button
                        mt={4}
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        onClick={() => setEscopo('historico')}
                      >
                        Conferir o histórico completo
                      </Button>
                    )}
                  </GlassCard>
                ) : (
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
                    {checksAtivos.map((ch, i) => (
                      <CheckCard
                        key={ch.chave}
                        check={ch}
                        index={i}
                        escopo={escopo}
                        qAtivo={!!f.q}
                        onClick={() => abrirDrill(ch.chave)}
                      />
                    ))}
                  </SimpleGrid>
                )}

                {!tudoLimpo && checksLimpos.length > 0 && (
                  <GlassCard intensity="low" p={3} mt={4}>
                    <Flex align="center" gap={2} wrap="wrap">
                      <CheckCircle size={16} weight="duotone" color="#16A34A" />
                      <Text fontSize="xs" fontWeight={700} color={FIN.muted}>
                        Sem ocorrências no recorte:
                      </Text>
                      {checksLimpos.map(c => (
                        <Text
                          key={c.chave}
                          fontSize="xs"
                          color={FIN.muted}
                          px={2}
                          py={0.5}
                          border="1px solid"
                          borderColor={FIN.hair}
                          borderRadius="full"
                        >
                          {escopo === 'periodo' && (c.qtd_historico ?? 0) > 0
                            ? `${c.titulo} — ${c.qtd_historico} no histórico`
                            : `✓ ${c.titulo}`}
                        </Text>
                      ))}
                    </Flex>
                  </GlassCard>
                )}
              </motion.div>
            )}

            {tab === 'pontualidade' && (
              <motion.div
                key="pontualidade"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <PontualidadeCard pont={pont} />
              </motion.div>
            )}
          </Box>
        )}
      </Box>

      {/* ── Modal de drill ──────────────────────────────────────────── */}
      <Modal
        isOpen={!!drillChave}
        onClose={() => setDrillChave(null)}
        size="4xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent maxH="82vh" borderRadius="2xl" overflow="hidden" mx={4}>
          <ModalHeader pb={2} borderBottom="1px solid" borderColor={FIN.hair}>
            <Flex align="center" gap={2}>
              <Box w="8px" h="8px" borderRadius="full" bg={drillSev.cor} />
              <Text fontSize="md" color={FIN.ink}>
                {drill?.titulo ?? 'Ocorrências'}
              </Text>
            </Flex>
            <Text fontSize="xs" fontWeight={500} color={FIN.muted} mt={0.5}>
              {drill
                ? `${totalReal.toLocaleString('pt-BR')} ocorrências no recorte — conferir/corrigir no Conta Azul`
                : ''}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={0} pb={5} px={{ base: 3, md: 6 }}>
            {/* toolbar: busca interna + contador (sólida — nada vaza por trás) */}
            <Flex
              position="sticky"
              top={0}
              zIndex={2}
              py={2.5}
              gap={3}
              align="center"
              bg="white"
              borderBottom="1px solid"
              borderColor={FIN.hair}
              boxShadow="0 4px 10px -6px rgba(15,23,42,0.08)"
            >
              <InputGroup size="sm" maxW="320px">
                <InputLeftElement>
                  <MagnifyingGlass size={14} color={FIN.faint} />
                </InputLeftElement>
                <Input
                  borderRadius="full"
                  placeholder="Filtrar por cliente, descrição ou ID…"
                  value={drillBusca}
                  onChange={e => setDrillBusca(e.target.value)}
                />
              </InputGroup>
              <Text ml="auto" fontSize="xs" color={FIN.faint} className="tabular-nums">
                mostrando {drill?.itens.length ?? 0} de {totalReal.toLocaleString('pt-BR')}
              </Text>
            </Flex>
            {drillLoading && !drill ? (
              <Flex justify="center" py={8}>
                <Spinner color={ACCENT} />
              </Flex>
            ) : (
              <Box overflowX="auto" opacity={drillLoading ? 0.55 : 1} transition="opacity 0.15s">
                <Box minW="600px">
                  <Box
                    display="grid"
                    gridTemplateColumns="minmax(140px,1.8fr) minmax(80px,1fr) 96px 96px 88px 88px"
                    gap={2}
                    px={3}
                    py={2}
                    fontSize="10px"
                    fontWeight={800}
                    letterSpacing="0.04em"
                    textTransform="uppercase"
                    color={FIN.faint}
                    borderBottom="1px solid"
                    borderColor={FIN.hair}
                  >
                    {headerCells.map(cell => {
                      const key = cell.slot ? sortCols[cell.slot] : undefined;
                      const ativo = key && sort?.key === key;
                      return (
                        <Flex
                          key={cell.label}
                          as={key ? 'button' : undefined}
                          onClick={() => clickSort(key)}
                          justify={cell.right ? 'flex-end' : 'flex-start'}
                          align="center"
                          gap={0.5}
                          cursor={key ? 'pointer' : 'default'}
                          color={ativo ? FIN.ink : FIN.faint}
                          textTransform="uppercase"
                          fontWeight={800}
                        >
                          {cell.label}
                          {ativo &&
                            (sort?.dir === 'asc' ? (
                              <CaretUp size={10} weight="bold" />
                            ) : (
                              <CaretDown size={10} weight="bold" />
                            ))}
                        </Flex>
                      );
                    })}
                  </Box>
                  {(drill?.itens ?? []).map(it => (
                    <DrillRow
                      key={`${it.id ?? 'x'}-${it.data_pagamento ?? ''}-${it.pago ?? ''}-${it.detalhe ?? ''}`}
                      item={it}
                      onCopy={copiarId}
                    />
                  ))}
                  {drill && drill.itens.length === 0 && (
                    <Text fontSize="sm" color={FIN.faint} py={4}>
                      Nenhuma ocorrência para essa busca.
                    </Text>
                  )}
                </Box>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </GlassPageBg>
  );
};

/** Aba de pontualidade: KPIs + ranking com filtro rápido de cliente. */
const PontualidadeCard = ({
  pont,
}: {
  pont?: {
    pct_em_dia: number;
    atraso_medio: number;
    qtd_atrasadores: number;
    ranking: {
      cliente: string | null;
      titulos: number;
      atrasos: number;
      atraso_medio: number;
      atraso_max: number;
    }[];
  };
}) => {
  const [filtro, setFiltro] = useState('');
  const ranking = (pont?.ranking ?? []).filter(
    c => !filtro || (c.cliente ?? '').toLowerCase().includes(filtro.toLowerCase()),
  );
  return (
    <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
      <Flex align="center" gap={1} mb={3} wrap="wrap">
        <Clock size={18} weight="duotone" color={ACCENT} />
        <Text fontWeight={700} color={FIN.ink}>
          Pontualidade de recebimento
        </Text>
        <InfoPopover title="Pontualidade">
          Atraso real = data de baixa − vencimento (só recebíveis). O período recorta pela{' '}
          <b>data da baixa</b> (recebimentos do período); a busca recorta o cliente. Mostra quem
          mais demora a pagar.
        </InfoPopover>
        <InputGroup size="sm" maxW="240px" ml="auto">
          <InputLeftElement>
            <MagnifyingGlass size={13} color={FIN.faint} />
          </InputLeftElement>
          <Input
            borderRadius="full"
            placeholder="Filtrar cliente…"
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
          />
        </InputGroup>
      </Flex>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} mb={4}>
        <PontKpi label="Pago em dia" valor={pont ? fmtPct(pont.pct_em_dia) : '—'} cor="#16A34A" />
        <PontKpi
          label="Atraso médio"
          valor={pont ? `${pont.atraso_medio.toFixed(1).replace('.', ',')} dias` : '—'}
          cor="#D97706"
        />
        <PontKpi
          label="Clientes que atrasam"
          valor={pont ? String(pont.qtd_atrasadores) : '—'}
          cor={FIN.ink}
        />
      </SimpleGrid>
      <Box
        display="grid"
        gridTemplateColumns="minmax(120px,2fr) 72px 72px 88px 72px"
        gap={2}
        px={3}
        py={2}
        fontSize="10px"
        fontWeight={800}
        letterSpacing="0.04em"
        textTransform="uppercase"
        color={FIN.faint}
        borderBottom="1px solid"
        borderColor={FIN.hair}
      >
        <Text>Cliente</Text>
        <Text textAlign="right">Títulos</Text>
        <Text textAlign="right">Atrasos</Text>
        <Text textAlign="right">Atraso méd.</Text>
        <Text textAlign="right">Máx</Text>
      </Box>
      {ranking.map(c => (
        <Box
          key={`${c.cliente ?? 'sem-nome'}-${c.titulos}-${c.atrasos}-${c.atraso_max}`}
          display="grid"
          gridTemplateColumns="minmax(120px,2fr) 72px 72px 88px 72px"
          gap={2}
          px={3}
          py={2}
          alignItems="center"
          borderBottom="1px solid"
          borderColor={FIN.hair}
          fontSize="xs"
        >
          <Text color={FIN.ink} noOfLines={1}>
            {c.cliente || '—'}
          </Text>
          <Text textAlign="right" color={FIN.muted}>
            {c.titulos}
          </Text>
          <Text textAlign="right" color="#D97706" fontWeight={700}>
            {c.atrasos}
          </Text>
          <Text textAlign="right" color={FIN.ink}>
            {c.atraso_medio.toFixed(1).replace('.', ',')}d
          </Text>
          <Text textAlign="right" color={FIN.faint}>
            {c.atraso_max}d
          </Text>
        </Box>
      ))}
      {pont && ranking.length === 0 && (
        <Text fontSize="sm" color={FIN.faint} py={4}>
          {filtro ? 'Nenhum cliente para essa busca.' : 'Nenhum atraso no recorte selecionado.'}
        </Text>
      )}
    </GlassCard>
  );
};
